import {
  canChangeApplicationSetting,
  canReadProperties,
  canWriteProperties,
  coreQueryKey,
} from '@/api/core/authorization';
import {
  readNativeCatalog,
  readNativeProperty,
  readNativeType,
  readNativeTypeBriefs,
} from '@/api/core/native-property-read';
import { changePropertyApplicationSetting } from '@/api/core/properties';
import { isApiError } from '@/api/http/errors';
import { ShellHeader } from '@/app/shell-header';
import { useOperatorSession } from '@/auth/runtime';
import { DirtyGuardProvider } from '@/components/dirty-guard';
import { IdText } from '@/components/id-text';
import { LoadingAnnouncement } from '@/components/loading-announcement';
import { isValidSearchText } from '@/lib/search-text';
import { isUuid } from '@/lib/validation';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  DetailPage,
  PageHeader,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useInsertionEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router';
import { RoomOnlineAccessSection } from '../access/room-online-access-section';
import { ApplicationSettingForm } from './application-setting-form';
import { HomepagePreviewDialog } from './homepage-preview-dialog';
import { MediaWriteOwnerProvider } from './media/media-write-form';
import { NativeAssetMediaSection } from './media/native-asset-media-section';
import { NativeTypeMediaSection } from './media/native-type-media-section';
import { nativeCatalogFloorsQuery } from './native-floors-queries';
import { LongMonthPricingSection } from './native-long-month-pricing-section';
import { NativeMidtermPreparation } from './native-midterm-preparation';
import { NativePropertyFaq } from './native-property-faq';
import { NativePropertyListPage } from './native-property-list-page';
import { nativePropertyListReturnTo, readNativePropertyNameHint } from './native-property-url';
import {
  AccessCoverage,
  AssetFacts,
  ExternalIntegrationZone,
  TypeReferenceValues,
} from './native-property-zones';
import { NativePublicSalesSection } from './native-public-sales-section';
import { NativeRoomSalesSection } from './native-room-sales-section';
import { NativeUnitSalesSection } from './native-unit-sales-section';
import { PresentationEditor } from './presentation/presentation-editor';
import { PropertyAccessOverview } from './property-access-overview';
import { PropertyFailure } from './property-feedback';
import {
  ApplicationSettingRefreshError,
  propertyRead,
  propertyRuntime,
  type PropertyRuntime,
} from './property-queries';
import {
  observeUnitClassificationParentOwner,
  UnitSaleClassificationSection,
} from './rooms/unit-sale-classification-section';
import { emptyFilteredTitle, SalesOrderControls, type SalesOrder } from './sales-order-controls';
import { TypeSalesTransitionSection } from './type-sales-transition-section';

/** 이탈 가드는 화면 전체에서 blocker 하나만 둔다(숨겨 둔 판매 탭·외부 연동 슬롯 패널·콘텐츠 편집이 서로 가리지 않게). */
export function NativePropertyReadPage(props: { runtime?: PropertyRuntime }) {
  return (
    <DirtyGuardProvider>
      <NativePropertyReadPageBody {...props} />
    </DirtyGuardProvider>
  );
}

function NativePropertyReadPageBody({ runtime = propertyRuntime }: { runtime?: PropertyRuntime }) {
  const session = useOperatorSession(runtime.session);
  const queryClient = useQueryClient();
  const { propertyId } = useParams();
  const [params, setParams] = useSearchParams();
  // 목록에서 들어오면 이름을 이미 안다 — 지점 정보를 읽는 동안 경로에 "지점 상세" 대신 이 이름을 보여 준다.
  const nameHint = readNativePropertyNameHint(useLocation().state, propertyId);
  const cursor = params.get('cursor');
  const sections = [
    { id: 'basic', label: '기본 자산 정보', short: '자산' },
    { id: 'spaces', label: '객실·슬롯', short: '객실' },
    { id: 'content', label: '홈페이지 콘텐츠', short: '콘텐츠' },
    { id: 'external', label: '외부 연동', short: '연동' },
    { id: 'sales', label: '가격·판매', short: '가격' },
    { id: 'access', label: '출입', short: '출입' },
  ];
  const section = sections.find((item) => item.id === params.get('section'))?.id ?? 'basic';
  const stickyContextRef = useRef<HTMLDivElement>(null);
  const sectionHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    // 폭이 모자라 탭 줄이 가로로 스크롤될 때도 지금 탭이 보이는 곳에 있게 한다.
    const tab = document.getElementById(`workspace-tab-${section}`);
    const scroll = tab && Reflect.get(tab, 'scrollIntoView');
    if (tab && typeof scroll === 'function')
      scroll.call(tab, { inline: 'nearest', block: 'nearest' });
  }, [section]);
  const previousSection = useRef(section);
  useEffect(() => {
    if (previousSection.current === section) return;
    previousSection.current = section;
    const heading = sectionHeading.current;
    heading?.focus({ preventScroll: true });
    // 헤딩을 맨 위에 붙이면(scrollIntoView) 본문 위 여백(py-6)만큼 잘린 채 시작한다 — 새 탭은 스크롤 상자의 맨 위에서 시작한다.
    for (let node = heading?.parentElement; node; node = node.parentElement) {
      if (node.scrollTop > 0 && /auto|scroll/.test(getComputedStyle(node).overflowY)) {
        node.scrollTop = 0;
        break;
      }
    }
  }, [section]);
  // 판매 탭은 처음 열 때 마운트하고 이후에는 유지한다. 다른 탭에서 가격·판매 API를 미리 부르지 않으면서,
  // 저장 전 입력값은 탭을 오가도 보존한다.
  const [salesVisited, setSalesVisited] = useState(section === 'sales');
  if (section === 'sales' && !salesVisited) setSalesVisited(true);
  const [contentVisited, setContentVisited] = useState(section === 'content');
  if (section === 'content' && !contentVisited) setContentVisited(true);
  function selectSection(id: string) {
    const next = new URLSearchParams(params);
    next.set('section', id);
    setParams(next);
  }

  const selectedTypeId = params.get('type');
  const roomTypeId = selectedTypeId;
  const [typeSelectorOpen, setTypeSelectorOpen] = useState(false);
  const [typePage, setTypePage] = useState<{ propertyId: string; cursor: string } | null>(null);
  const typeCursor = typePage && typePage.propertyId === propertyId ? typePage.cursor : null;
  const briefEnabled =
    runtime.nativeTypeBriefReadEnabled === true && (section === 'spaces' || typeSelectorOpen);
  const roomSortBy = params.get('room_sort_by') === 'name' ? 'name' : 'floor';
  const roomSortDirection = params.get('room_sort_direction') === 'desc' ? 'desc' : 'asc';
  // 검색·층 필터는 서버가 거른다. 주소의 값이 잘못되면(길이·형식) 필터 없음으로 읽는다.
  const rawRoomQ = (params.get('room_q') ?? '').trim();
  const roomQ = rawRoomQ !== '' && isValidSearchText(rawRoomQ) ? rawRoomQ : '';
  const rawRoomFloor = params.get('room_floor');
  const roomFloorIsNull = rawRoomFloor === 'null';
  const roomFloor =
    rawRoomFloor !== null &&
    /^-?\d{1,10}$/.test(rawRoomFloor) &&
    Number.isInteger(Number(rawRoomFloor)) &&
    Math.abs(Number(rawRoomFloor)) < 2 ** 31
      ? Number(rawRoomFloor)
      : null;
  const roomOrder: SalesOrder = {
    sortBy: roomSortBy,
    sortDirection: roomSortDirection,
    floor: roomFloor,
    floorIsNull: roomFloorIsNull,
    q: roomQ,
  };
  const roomFilterActive = roomQ !== '' || roomFloor !== null || roomFloorIsNull;
  const roomFilterKey = JSON.stringify([
    roomQ,
    roomFloor,
    roomFloorIsNull,
    roomSortBy,
    roomSortDirection,
  ]);
  // Route cursors start unfiltered. A filtered cursor is usable only in the scope that issued it.
  const [roomCursorScope, setRoomCursorScope] = useState<{
    propertyId: string;
    typeId: string;
    cursor: string;
    filterKey: string;
  } | null>(null);
  const catalogCursor = roomTypeId
    ? roomCursorScope &&
      roomCursorScope.propertyId === propertyId &&
      roomCursorScope.typeId === roomTypeId &&
      roomCursorScope.filterKey === roomFilterKey &&
      roomCursorScope.cursor === cursor
      ? cursor
      : null
    : cursor;
  // 헤더 경로의 링크와 선택 버튼이 같은 주소 규칙을 쓰도록, 이동한 뒤의 쿼리를 따로 만든다.
  function typeSelectionParams(id: string | null) {
    const next = new URLSearchParams(params);
    if (id) next.set('type', id);
    else next.delete('type');
    next.delete('room');
    next.delete('slot');
    if (id !== selectedTypeId) {
      next.delete('cursor');
      // 층 선택지는 유형마다 다르다. 검색어와 정렬은 유지하고 층 조건만 푼다.
      next.delete('room_floor');
    }
    return next;
  }
  function selectType(id: string | null) {
    setParams(typeSelectionParams(id));
  }
  const selectedRoomId = params.get('room');
  const selectedUnitId = params.get('slot');
  const unitCommandBoundary = useRef<object>({});
  const unitSelection = useRef({
    propertyId,
    roomId: selectedRoomId,
    unitId: selectedUnitId,
    typeId: selectedTypeId,
  });
  useInsertionEffect(() => {
    unitSelection.current = {
      propertyId,
      roomId: selectedRoomId,
      unitId: selectedUnitId,
      typeId: selectedTypeId,
    };
    observeUnitClassificationParentOwner(unitCommandBoundary.current, {
      runtime,
      generation: session.generation,
      propertyId: propertyId ?? '',
      branchId: null,
      authorization: session.authorization,
      sessionStatus: session.status,
      propertyDenied: session.deniedDomains.includes('property'),
      enabled: true,
    });
  }, [
    runtime,
    session.generation,
    session.authorization,
    session.status,
    session.deniedDomains,
    propertyId,
    selectedRoomId,
    selectedUnitId,
    selectedTypeId,
  ]);
  // 객실 목록 안에서 고르거나 닫을 때는 보던 쪽(cursor)을 유지한다. 다른 탭·유형에서 들어오면 쪽을 버린다.
  function selectRoom(id: string | null, options: { keepPage?: boolean } = {}) {
    const next = new URLSearchParams(params);
    if (id) next.set('room', id);
    else next.delete('room');
    next.delete('slot');
    if (!options.keepPage) next.delete('cursor');
    setParams(next);
  }
  // 정렬·검색·층 조건이 바뀌면 cursor만 버린다. 객실·슬롯 선택은 그대로 둬 작성 중인 초안을 지키지 않을 이유가 없다.
  function changeRoomOrder(next: SalesOrder) {
    const nextParams = new URLSearchParams(params);
    const setOrDelete = (key: string, value: string | null) => {
      if (value === null || value === '') nextParams.delete(key);
      else nextParams.set(key, value);
    };
    setOrDelete('room_sort_by', next.sortBy === 'floor' ? null : next.sortBy);
    setOrDelete('room_sort_direction', next.sortDirection === 'asc' ? null : next.sortDirection);
    setOrDelete('room_q', next.q);
    setOrDelete(
      'room_floor',
      next.floorIsNull ? 'null' : next.floor === null ? null : String(next.floor),
    );
    nextParams.delete('cursor');
    setParams(nextParams);
  }
  function unitSelectionParams(id: string | null) {
    const next = new URLSearchParams(params);
    if (id) next.set('slot', id);
    else next.delete('slot');
    return next;
  }
  function selectUnit(id: string | null) {
    setParams(unitSelectionParams(id));
  }
  const roomsHeadingRef = useRef<HTMLHeadingElement>(null);
  const selectedRoomHeadingRef = useRef<HTMLHeadingElement>(null);
  const selectedUnitHeadingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (section !== 'spaces') return;
    const heading = selectedUnitId
      ? selectedUnitHeadingRef.current
      : selectedRoomId
        ? selectedRoomHeadingRef.current
        : roomsHeadingRef.current;
    heading?.focus({ preventScroll: true });
    const scroll = heading && Reflect.get(heading, 'scrollIntoView');
    if (heading && typeof scroll === 'function') scroll.call(heading, { block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Section changes focus the workspace heading separately.
  }, [selectedRoomId, selectedUnitId]);
  const canConnectRooms =
    session.status === 'authorized' &&
    !session.deniedDomains.includes('property') &&
    !!session.authorization &&
    canWriteProperties(session.authorization);
  const detailReturnTo = nativePropertyListReturnTo(
    params.getAll('returnTo').length === 1 ? params.get('returnTo') : null,
  );
  const canRead =
    session.status === 'authorized' &&
    !session.deniedDomains.includes('property') &&
    !!session.authorization &&
    canReadProperties(session.authorization);
  const propertyBasic = useQuery({
    queryKey: [
      ...coreQueryKey(
        runtime.apiNamespace,
        session.generation,
        'native-property-catalog',
        propertyId ?? '',
      ),
      'basic',
    ],
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        session.generation,
        () => readNativeProperty(runtime.client, propertyId ?? '', signal),
        signal,
        () => unitSelection.current.propertyId === propertyId,
      ),
    enabled: canRead && Boolean(propertyId),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  });
  const catalogEnabled =
    canRead &&
    Boolean(propertyId && selectedTypeId) &&
    (section === 'spaces' || section === 'sales');
  const catalog = useQuery({
    queryKey: [
      ...coreQueryKey(
        runtime.apiNamespace,
        session.generation,
        'native-property-catalog',
        propertyId ?? '',
      ),
      roomTypeId,
      catalogCursor,
      roomSortBy,
      roomSortDirection,
      roomQ,
      roomFloor,
      roomFloorIsNull,
    ],
    queryFn: async ({ signal }) => {
      try {
        if (!propertyId) throw new Error('선택한 지점이 없습니다.');
        return await readNativeCatalog(runtime.client, propertyId, catalogCursor, signal, {
          roomTypeId,
          sortBy: roomSortBy,
          sortDirection: roomSortDirection,
          q: roomQ,
          floor: roomFloor,
          floorIsNull: roomFloorIsNull,
        });
      } catch (error) {
        if (
          !signal.aborted &&
          isApiError(error) &&
          error.status === 403 &&
          runtime.session.getSnapshot().generation === session.generation
        )
          runtime.session.reportDomainAccessDenied('property', session.generation);
        throw error;
      }
    },
    enabled: catalogEnabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  });
  const floorFacet = useQuery(
    nativeCatalogFloorsQuery(
      runtime,
      session.generation,
      propertyId ?? '',
      roomTypeId,
      catalogEnabled && section === 'spaces',
    ),
  );
  const typeBrief = useQuery({
    queryKey: [
      ...coreQueryKey(
        runtime.apiNamespace,
        session.generation,
        'native-property-catalog',
        propertyId ?? '',
      ),
      'type-brief',
      typeCursor,
    ],
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        session.generation,
        () => readNativeTypeBriefs(runtime.client, propertyId ?? '', typeCursor, signal),
        signal,
        () => unitSelection.current.propertyId === propertyId,
      ),
    enabled: canRead && Boolean(propertyId) && briefEnabled,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  });
  const roomOnPage = catalog.data?.data.rooms.find(
    (room) =>
      room.id === selectedRoomId && (!selectedTypeId || room.room_type_id === selectedTypeId),
  );
  const selectedCatalog = useQuery({
    queryKey: [
      ...coreQueryKey(
        runtime.apiNamespace,
        session.generation,
        'native-property-catalog',
        propertyId ?? '',
      ),
      'selected-room',
      selectedRoomId,
      selectedTypeId,
    ],
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        session.generation,
        () =>
          readNativeCatalog(runtime.client, propertyId ?? '', null, signal, {
            roomId: selectedRoomId,
            roomTypeId: selectedTypeId,
          }),
        signal,
        () =>
          unitSelection.current.propertyId === propertyId &&
          unitSelection.current.roomId === selectedRoomId &&
          unitSelection.current.typeId === selectedTypeId,
      ),
    enabled:
      canRead &&
      Boolean(propertyId && selectedRoomId) &&
      propertyBasic.isSuccess &&
      (!catalogEnabled || catalog.isSuccess) &&
      !roomOnPage,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  });
  const typeInCatalog =
    catalog.data?.data.room_types.find((type) => type.id === selectedTypeId) ??
    selectedCatalog.data?.data.room_types.find((type) => type.id === selectedTypeId);
  const typeDetail = useQuery({
    queryKey: [
      ...coreQueryKey(
        runtime.apiNamespace,
        session.generation,
        'native-property-catalog',
        propertyId ?? '',
      ),
      'selected-type',
      selectedTypeId,
    ],
    queryFn: ({ signal }) =>
      propertyRead(
        runtime,
        session.generation,
        () => readNativeType(runtime.client, propertyId ?? '', selectedTypeId ?? '', signal),
        signal,
        () =>
          unitSelection.current.propertyId === propertyId &&
          unitSelection.current.typeId === selectedTypeId,
      ),
    enabled:
      canRead &&
      Boolean(propertyId && selectedTypeId) &&
      !selectedRoomId &&
      !catalogEnabled &&
      !typeInCatalog,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  });
  // Measure the compact controls, including wrapped names and responsive tabs.
  useLayoutEffect(() => {
    const sticky = stickyContextRef.current;
    if (!sticky) return;
    const measure = () => {
      sticky.parentElement?.style.setProperty(
        '--workspace-sticky-height',
        `${String(sticky.getBoundingClientRect().height)}px`,
      );
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(sticky);
    return () => observer?.disconnect();
  });
  const nextCatalogCursor = catalog.data?.next_cursor;
  if (
    session.status !== 'authorized' ||
    session.deniedDomains.includes('property') ||
    !session.authorization ||
    !canReadProperties(session.authorization)
  )
    return null;
  if (propertyId) {
    const current = propertyBasic.data
      ? {
          property: propertyBasic.data,
          room_types: catalog.data?.data.room_types ?? selectedCatalog.data?.data.room_types ?? [],
          rooms: catalog.data?.data.rooms ?? [],
        }
      : undefined;
    const types = new Map((current?.room_types ?? []).map((type) => [type.id, type.name]));
    const selectedType = typeInCatalog ?? typeDetail.data;
    const selectedRoom =
      roomOnPage ??
      (selectedCatalog.isSuccess
        ? selectedCatalog.data.data.rooms.find((room) => room.id === selectedRoomId)
        : undefined);
    const visibleRooms = selectedType
      ? (current?.rooms ?? []).filter((room) => room.room_type_id === selectedType.id)
      : selectedRoom
        ? [selectedRoom]
        : [];
    const selectedUnit = selectedRoom?.units.find((unit) => unit.id === selectedUnitId);
    const contextType =
      current?.room_types.find((type) => type.id === selectedRoom?.room_type_id) ??
      selectedCatalog.data?.data.room_types.find(
        (type) => type.id === selectedRoom?.room_type_id,
      ) ??
      selectedType;
    const typeLabel =
      contextType?.name ?? typeBrief.data?.items.find((type) => type.id === selectedTypeId)?.name;
    let selectionResolved = true;
    if (selectedTypeId !== null) selectionResolved = contextType?.id === selectedTypeId;
    if (selectedRoomId !== null) selectionResolved = Boolean(selectedRoom && contextType);
    if (selectedUnitId !== null)
      selectionResolved = Boolean(selectedRoom && selectedUnit && contextType);
    const selectionError = selectedRoomId
      ? selectedCatalog.error
      : selectedTypeId
        ? typeDetail.error
        : null;
    const invalidSelection = [selectedTypeId, selectedRoomId, selectedUnitId].some(
      (id) => id !== null && !isUuid(id),
    );
    const invalidSlot = Boolean(
      selectedUnitId && (!selectedRoomId || (selectedRoom && !selectedUnit)),
    );
    const unitCount = visibleRooms.reduce((sum, room) => sum + room.units.length, 0);
    // 대상 선택은 홈페이지 콘텐츠 탭에서만 안내 카드 아래에 둔다(안내와 미리보기가 먼저, 편집 대상 선택은 그다음).
    /* eslint-disable @mgrvtechgroup/admin-ds/no-hand-rolled-screen -- Bound long control labels only; DetailPage retains the screen width budget. */
    const targetSelector = current ? (
      <section aria-label="대상 선택" className="space-y-3 rounded-md border p-3">
        <details
          open={section === 'spaces' || undefined}
          onToggle={(event) => {
            setTypeSelectorOpen(event.currentTarget.open);
          }}
        >
          <summary className="cursor-pointer font-medium">
            객실 유형 선택 · {typeLabel ?? (selectedTypeId ? '객실 유형 조회 중' : '지점 전체')}
          </summary>

          {runtime.nativeTypeBriefReadEnabled === true ? null : (
            <p role="status">객실 유형 목록 조회가 아직 연결되지 않았습니다.</p>
          )}
          {briefEnabled && typeBrief.isFetching ? (
            <LoadingAnnouncement visible>객실 유형을 불러오고 있습니다.</LoadingAnnouncement>
          ) : null}
          {briefEnabled && typeBrief.isError ? (
            <PropertyFailure
              error={typeBrief.error}
              onRetry={() => {
                void typeBrief.refetch();
              }}
            />
          ) : null}

          <div className="mt-3 flex max-h-32 flex-wrap gap-2 overflow-y-auto sm:max-h-48">
            <Button
              variant={contextType ? 'outline' : 'solid'}
              onClick={() => {
                selectType(null);
              }}
            >
              지점으로 돌아가기
            </Button>
            {(typeBrief.data?.items ?? []).map((type) => (
              <Button
                key={type.id}
                className="h-auto max-w-full [overflow-wrap:anywhere] whitespace-normal"
                variant={
                  selectedTypeId === type.id || (!selectedTypeId && contextType?.id === type.id)
                    ? 'solid'
                    : 'outline'
                }
                aria-pressed={
                  selectedTypeId === type.id || (!selectedTypeId && contextType?.id === type.id)
                }
                onClick={() => {
                  selectType(type.id);
                }}
              >
                {type.name} 객실 보기
              </Button>
            ))}
          </div>
          {typeBrief.data?.next_cursor ? (
            <Button
              variant="outline"
              onClick={() => {
                const next = typeBrief.data.next_cursor;
                if (next) setTypePage({ propertyId, cursor: next });
              }}
            >
              다음 객실 유형
            </Button>
          ) : null}
          {typeCursor ? (
            <Button
              variant="outline"
              onClick={() => {
                setTypePage(null);
              }}
            >
              첫 객실 유형
            </Button>
          ) : null}
        </details>
      </section>
    ) : null;
    /* eslint-enable @mgrvtechgroup/admin-ds/no-hand-rolled-screen */
    // 경로는 앱 셸 헤더의 브레드크럼이다: 지점 목록 → 지점 → 객실 유형 → 객실 → 슬롯.
    // 마지막 단계가 현재 위치(링크 아님)이고 앞 단계는 링크라 ⌘/Ctrl 클릭으로 새 탭에 열 수 있다.
    // 단계 링크는 지금 주소에서 쿼리만 바꾸므로 업무 탭·정렬·목록 복귀 주소(returnTo)는 그대로 남는다.
    // 경로가 곧 헤더의 제목이다 — 지점명이 경로와 제목에 두 번 나오지 않게 합쳤다. 현재 단계만 굵게 보인다.
    // 지점 정보를 읽는 동안에도 목록에서 넘어온 이름이 있으면 첫 단계로 보여 준다(깊은 단계는 읽은 뒤에 붙는다).
    const propertyName = current?.property.name ?? nameHint;
    const scopeTrail = [
      ...(propertyName
        ? [
            {
              key: 'property',
              label: propertyName,
              to: `?${typeSelectionParams(null).toString()}`,
            },
          ]
        : []),
      ...(current && typeLabel
        ? [
            {
              key: 'type',
              label: typeLabel,
              to: `?${typeSelectionParams(contextType?.id ?? selectedTypeId).toString()}`,
            },
          ]
        : []),
      ...(current && selectedRoom
        ? [
            {
              key: 'room',
              label: selectedRoom.name,
              to: `?${unitSelectionParams(null).toString()}`,
            },
          ]
        : []),
      ...(current && selectedUnit
        ? [{ key: 'unit', label: selectedUnit.name, to: `?${params.toString()}` }]
        : []),
    ];
    // 이름을 아직 모르면(주소로 바로 들어옴·새 탭) 읽는 동안 이름 자리를 스켈레톤으로 비워 둔다 — "지점 상세"가 먼저 뜨고
    // 이름으로 바뀌는 깜빡임이 없게. 읽기가 실패해 이름을 끝내 모르면 스켈레톤 대신 "지점 상세"를 둔다.
    const breadcrumb = (
      <Breadcrumb aria-label="공간 탐색 경로">
        {/* 글자 크기는 항목(li)에 준다 — BreadcrumbList가 body(14px)를 직접 갖고 있어 같은 요소에 body-lg를 주면 CSS 순서에 따라 결과가 갈린다. */}
        <BreadcrumbList className="[&_li]:body-lg flex-nowrap">
          <BreadcrumbItem className="shrink-0">
            <BreadcrumbLink
              render={
                <Link aria-label="지점 목록으로" to={detailReturnTo ?? '/properties'}>
                  지점
                </Link>
              }
            />
          </BreadcrumbItem>
          <BreadcrumbSeparator className="shrink-0" />
          {scopeTrail.length === 0 ? (
            <BreadcrumbItem>
              {propertyBasic.isPending ? (
                <>
                  <BreadcrumbPage className="sr-only">지점 상세</BreadcrumbPage>
                  <Skeleton aria-hidden className="h-4 w-24" />
                </>
              ) : (
                <BreadcrumbPage className="font-semibold">지점 상세</BreadcrumbPage>
              )}
            </BreadcrumbItem>
          ) : (
            scopeTrail.map((crumb, index) => {
              const last = index === scopeTrail.length - 1;
              return (
                <Fragment key={crumb.key}>
                  {/* 좁은 폭에서는 긴 이름이 먼저 말줄임된다. 객실 번호 같은 짧은 이름까지 같은 비율로 줄이면 한두 글자만 남는다. */}
                  <BreadcrumbItem className={crumb.label.length <= 4 ? 'shrink-0' : 'min-w-0'}>
                    {last ? (
                      <BreadcrumbPage className="truncate font-semibold" title={crumb.label}>
                        {crumb.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink
                        render={
                          <Link className="truncate" title={crumb.label} to={crumb.to}>
                            {crumb.label}
                          </Link>
                        }
                      />
                    )}
                  </BreadcrumbItem>
                  {!last && <BreadcrumbSeparator className="shrink-0" />}
                </Fragment>
              );
            })
          )}
        </BreadcrumbList>
      </Breadcrumb>
    );
    return (
      <DetailPage className="min-h-0 min-w-0 flex-1 scroll-pb-20 overflow-y-auto sm:scroll-pb-0">
        <ShellHeader>
          {/* 눈에 보이는 제목은 경로의 현재 단계가 맡는다. 페이지 제목(h1)은 지점명 그대로 보조 기술용으로 둔다. */}
          <PageHeader
            title={
              <>
                <h1 className="sr-only">{propertyName ?? '지점 상세'}</h1>
                {breadcrumb}
              </>
            }
            description="객실과 판매 준비 상태를 확인합니다."
          />
        </ShellHeader>
        <div className="flex min-w-0 flex-col gap-4 p-4 break-words sm:p-6">
          {typeDetail.isError && section !== 'content' ? (
            <PropertyFailure
              error={typeDetail.error}
              onRetry={() => {
                void typeDetail.refetch();
              }}
            />
          ) : null}
          {catalog.isFetching && !catalog.isPending ? (
            <LoadingAnnouncement visible>지점 상세를 새로 불러오고 있습니다.</LoadingAnnouncement>
          ) : null}
          {propertyBasic.isPending || (catalogEnabled && catalog.isPending) ? (
            <LoadingAnnouncement visible>지점 상세를 불러오고 있습니다.</LoadingAnnouncement>
          ) : null}
          {propertyBasic.isError ? (
            <PropertyFailure
              error={propertyBasic.error}
              onRetry={() => {
                void propertyBasic.refetch();
              }}
            />
          ) : catalogEnabled && catalog.isError ? (
            catalogCursor && isApiError(catalog.error) && catalog.error.status === 422 ? (
              <div role="alert" className="space-y-3 p-6">
                <p>객실 목록의 조회 기준이 변경되었습니다. 처음부터 다시 조회해 주세요.</p>
                <Button
                  variant="outline"
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    next.delete('cursor');
                    setRoomCursorScope(null);
                    setParams(next);
                  }}
                >
                  첫 객실 페이지 다시 조회
                </Button>
              </div>
            ) : roomTypeId && isApiError(catalog.error) && catalog.error.status === 404 ? (
              <div role="alert" className="p-6">
                <p className="font-semibold">지점 또는 선택한 객실 유형을 찾을 수 없습니다.</p>
                <p className="body-sm mt-2">지점 목록에서 다시 선택해 주세요.</p>
              </div>
            ) : (
              <PropertyFailure
                error={catalog.error}
                onRetry={() => {
                  void catalog.refetch();
                }}
              />
            )
          ) : current ? (
            <>
              <div ref={stickyContextRef} className="bg-background sticky top-0 z-10 border-b pb-1">
                <section aria-label="업무와 대상 선택" className="bg-background min-w-0">
                  <Tabs value={section} onValueChange={selectSection}>
                    <TabsList
                      aria-label="지점 업무"
                      className="flex h-auto w-full gap-1 overflow-x-auto lg:justify-start lg:gap-4"
                    >
                      {sections.map((item) => (
                        <TabsTrigger
                          key={item.id}
                          aria-label={item.label}
                          className="min-w-fit flex-1 px-1 lg:flex-none lg:px-2"
                          value={item.id}
                          id={`workspace-tab-${item.id}`}
                          aria-controls={`workspace-panel-${item.id}`}
                        >
                          <span className="lg:hidden">{item.short}</span>
                          <span className="hidden lg:inline">{item.label}</span>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </Tabs>
                </section>
              </div>
              {section !== 'content' && targetSelector}
              <h2
                ref={sectionHeading}
                tabIndex={-1}
                className="title-sm scroll-mt-[calc(var(--workspace-sticky-height,0px)+1rem)]"
              >
                {sections.find((item) => item.id === section)?.label}
              </h2>
              {salesVisited && section === 'sales' && selectedUnit && selectedRoom && (
                <section
                  aria-label="슬롯 가격 적용 범위"
                  className="space-y-2 rounded-md border p-4"
                >
                  <h3 className="font-semibold">{selectedUnit.name} 슬롯 가격</h3>
                  <p>
                    중기 일가격·판매는 {selectedRoom.name} 객실 단위로 적용됩니다. 아래 장기 월
                    가격은 선택한 슬롯에 적용됩니다. 처리 중인 객실 요청의 결과도 객실 화면에서
                    확인하세요.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      selectRoom(selectedRoom.id);
                    }}
                  >
                    {selectedRoom.name} 객실 가격·판매 보기
                  </Button>
                </section>
              )}
              {selectedRoom && selectedUnit && (section === 'spaces' || section === 'sales') && (
                <UnitSaleClassificationSection
                  key={`${session.generation.toString()}:${selectedUnit.id}:${section}`}
                  mode="native-initialization"
                  runtime={runtime}
                  ownerBoundary={unitCommandBoundary.current}
                  generation={session.generation}
                  propertyId={propertyId}
                  authorization={session.authorization}
                  room={{
                    id: selectedRoom.id,
                    topologyRevision: selectedRoom.topology_revision,
                  }}
                  slot={{ id: selectedUnit.id, roomId: selectedUnit.room_id }}
                  page={{
                    property: {
                      property_id: current.property.id,
                      branch_id: null,
                    },
                    rooms: [selectedRoom],
                    slots: selectedRoom.units.map((slot) => ({
                      id: slot.id,
                      roomId: slot.room_id,
                    })),
                  }}
                  isCurrent={() =>
                    runtime.session.getSnapshot().generation === session.generation &&
                    unitSelection.current.propertyId === propertyId &&
                    unitSelection.current.roomId === selectedRoom.id &&
                    unitSelection.current.unitId === selectedUnit.id
                  }
                />
              )}
              <div
                role="tabpanel"
                id="workspace-panel-basic"
                aria-labelledby="workspace-tab-basic"
                hidden={section !== 'basic'}
                className="space-y-4"
              >
                {section === 'basic' &&
                selectedRoomId &&
                !selectedRoom &&
                selectedCatalog.isPending ? (
                  <LoadingAnnouncement visible>
                    선택한 객실을 불러오고 있습니다.
                  </LoadingAnnouncement>
                ) : section === 'basic' &&
                  selectedTypeId &&
                  !selectedRoomId &&
                  !contextType &&
                  typeDetail.isPending ? (
                  <LoadingAnnouncement visible>객실 유형을 불러오고 있습니다.</LoadingAnnouncement>
                ) : (
                  <AssetFacts
                    type={contextType}
                    room={selectedRoom}
                    unit={selectedUnit}
                    typeRoomCount={catalogEnabled && catalog.isSuccess ? visibleRooms.length : null}
                    catalogComplete={!cursor && !nextCatalogCursor && !roomFilterActive}
                    catalogFiltered={roomFilterActive}
                  />
                )}
                <section aria-label="지점 등록 정보" className="rounded-md border p-4">
                  <h3 className="font-semibold">지점 등록 정보</h3>
                  <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                      <dt className="text-muted-foreground">지점명</dt>
                      <dd>{current.property.name}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">코드</dt>
                      <dd>{current.property.code}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">지점 ID</dt>
                      <dd>
                        <details>
                          <summary className="cursor-pointer">식별 정보</summary>
                          <IdText value={current.property.id} label="지점 ID" />
                        </details>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">신청 허용</dt>
                      <dd>{current.property.application_enabled ? '허용' : '차단'}</dd>
                    </div>
                  </dl>
                </section>
                {canChangeApplicationSetting(session.authorization) && (
                  <details className="rounded-md border p-4">
                    <summary className="cursor-pointer font-medium">신청 허용 수정</summary>
                    <ApplicationSettingForm
                      key={`${String(session.generation)}-${propertyId}-${String(current.property.version)}`}
                      canSubmit={!catalog.isFetching}
                      property={{
                        id: propertyId,
                        name: current.property.name,
                        enabled: current.property.application_enabled,
                        version: current.property.version,
                      }}
                      onSave={async (draft) => {
                        const latest = runtime.session.getSnapshot();
                        if (
                          latest.generation !== session.generation ||
                          latest.status !== 'authorized' ||
                          !latest.authorization ||
                          latest.deniedDomains.includes('property') ||
                          !canChangeApplicationSetting(latest.authorization)
                        )
                          throw new Error('현재 권한을 확인하세요.');
                        const result = await changePropertyApplicationSetting(
                          runtime.client,
                          propertyId,
                          draft,
                        );
                        await queryClient.invalidateQueries({
                          queryKey: coreQueryKey(
                            runtime.apiNamespace,
                            session.generation,
                            'native-property-catalog',
                            propertyId,
                          ),
                        });
                        const refreshed = await propertyBasic.refetch();
                        if (refreshed.isError) throw new ApplicationSettingRefreshError();
                        return result;
                      }}
                    />
                  </details>
                )}
              </div>
              <div
                role="tabpanel"
                id="workspace-panel-content"
                aria-labelledby="workspace-tab-content"
                hidden={section !== 'content'}
                className="space-y-4"
              >
                {contentVisited && (
                  <>
                    <section aria-label="홈페이지 노출 위치 안내" className="rounded-md border p-4">
                      <h2 className="font-semibold">홈페이지에 보이는 위치</h2>
                      <ul className="body-sm mt-2 list-disc space-y-1 pl-5">
                        <li>지점: 검색 결과 카드 사진, 상세 상단 대표 사진과 소개</li>
                        <li>객실 유형: 개인 공간의 사진 갤러리, 소개·가구·시설</li>
                        <li>슬롯 평면도: 슬롯 선택에서 확인하는 평면도</li>
                        <li>공용공간: 상세 하단의 공간별 사진과 설명</li>
                        <li>FAQ: 지점에 배정하고 상세 노출을 켠 질문·답변</li>
                      </ul>
                      <p className="body-sm text-muted-foreground mt-3">
                        편집할 대상은 아래 ‘객실 유형 선택’에서 고르고, 객실·슬롯은 ‘객실·슬롯’
                        탭에서 고르세요. 사진을 등록한 뒤에는 노출 용도·언어·순서와 게시 설정을
                        확인하세요. 저장한 내용이 홈페이지에서 어떻게 보이는지는 아래 미리보기로
                        확인할 수 있습니다.
                      </p>
                      {selectionResolved && (
                        <div className="mt-3">
                          <HomepagePreviewDialog
                            runtime={runtime}
                            generation={session.generation}
                            propertyId={propertyId}
                            typeId={contextType?.id}
                            roomId={selectedRoom?.id}
                          />
                        </div>
                      )}
                    </section>
                    {section === 'content' && targetSelector}
                    {!selectionResolved &&
                      section === 'content' &&
                      (selectionError ? (
                        <PropertyFailure
                          error={selectionError}
                          onRetry={() => {
                            if (selectedRoomId) void selectedCatalog.refetch();
                            else void typeDetail.refetch();
                          }}
                        />
                      ) : invalidSelection ? (
                        <p role="alert">선택한 대상의 식별 정보를 확인하지 못했습니다.</p>
                      ) : invalidSlot ? (
                        <p role="alert">선택한 슬롯을 현재 객실에서 확인하지 못했습니다.</p>
                      ) : (
                        <LoadingAnnouncement visible>
                          선택한 대상의 콘텐츠 범위를 확인하고 있습니다.
                        </LoadingAnnouncement>
                      ))}
                    {selectionResolved && (
                      <>
                        {section === 'content' && (
                          <PresentationEditor
                            key={`content-presentation:${selectedUnit?.id ?? selectedRoom?.id ?? contextType?.id ?? propertyId}`}
                            runtime={runtime}
                            propertyId={propertyId}
                            nativeScope={{
                              property: current.property,
                              type: contextType,
                              room: selectedRoom,
                              unit: selectedUnit,
                            }}
                            resource={
                              selectedUnit
                                ? 'units'
                                : selectedRoom
                                  ? 'rooms'
                                  : contextType
                                    ? 'room-types'
                                    : 'properties'
                            }
                            id={
                              selectedUnit?.id ?? selectedRoom?.id ?? contextType?.id ?? propertyId
                            }
                            label={
                              selectedUnit?.name ??
                              selectedRoom?.name ??
                              contextType?.name ??
                              current.property.name
                            }
                          />
                        )}
                        {selectedRoom ? (
                          <NativeAssetMediaSection
                            readEnabled={section === 'content'}
                            key={selectedUnit?.id ?? selectedRoom.id}
                            eager
                            runtime={runtime}
                            generation={session.generation}
                            propertyId={propertyId}
                            target={{
                              kind: selectedUnit ? 'SLOT' : 'ROOM',
                              id: selectedUnit?.id ?? selectedRoom.id,
                              floor: null,
                            }}
                            label={selectedUnit?.name ?? selectedRoom.name}
                            onEditRoomType={selectType}
                          />
                        ) : contextType ? (
                          <MediaWriteOwnerProvider
                            key={propertyId}
                            runtime={runtime}
                            propertyId={propertyId}
                          >
                            <NativeTypeMediaSection
                              readEnabled={section === 'content'}
                              key={contextType.id}
                              runtime={runtime}
                              generation={session.generation}
                              propertyId={propertyId}
                              typeId={contextType.id}
                              typeName={contextType.name}
                            />
                          </MediaWriteOwnerProvider>
                        ) : (
                          <NativeAssetMediaSection
                            readEnabled={section === 'content'}
                            key={`${propertyId}-media`}
                            eager
                            runtime={runtime}
                            generation={session.generation}
                            propertyId={propertyId}
                            target={{ kind: 'PROPERTY', id: null, floor: null }}
                            label={current.property.name}
                          />
                        )}
                        {contextType && (
                          <TypeReferenceValues
                            type={contextType}
                            level={selectedUnit ? 'unit' : selectedRoom ? 'room' : 'type'}
                          />
                        )}
                        <Button
                          variant="outline"
                          onClick={() => {
                            selectSection('sales');
                          }}
                        >
                          기간을 선택해 고객 공개 결과 확인
                        </Button>
                      </>
                    )}
                    <NativePropertyFaq
                      key={`${propertyId}-faq`}
                      runtime={runtime}
                      propertyId={propertyId}
                      generation={session.generation}
                    />
                  </>
                )}
              </div>
              <div
                role="tabpanel"
                id="workspace-panel-external"
                aria-labelledby="workspace-tab-external"
                hidden={section !== 'external'}
                className="space-y-4"
              >
                {section === 'external' && (
                  <ExternalIntegrationZone
                    runtime={runtime}
                    generation={session.generation}
                    propertyId={propertyId}
                    propertyName={current.property.name}
                    type={contextType}
                    room={selectedRoom}
                    selectionPending={
                      Boolean(selectedRoomId) && !selectedRoom && !selectedCatalog.isError
                    }
                    selectionError={
                      selectedCatalog.isError && !selectedRoom ? selectedCatalog.error : null
                    }
                    onRetrySelection={() => {
                      void selectedCatalog.refetch();
                    }}
                    selectedUnitId={selectedUnitId}
                    onSelectUnit={selectUnit}
                    onChooseSpace={() => {
                      selectSection('spaces');
                    }}
                    onSelectRoom={(id) => {
                      const next = new URLSearchParams(params);
                      next.set('room', id);
                      next.delete('type');
                      next.delete('slot');
                      next.delete('cursor');
                      setParams(next);
                    }}
                    onGoSales={() => {
                      selectSection('sales');
                    }}
                  />
                )}
              </div>
              <div
                role="tabpanel"
                id="workspace-panel-sales"
                aria-labelledby="workspace-tab-sales"
                hidden={section !== 'sales'}
                className="space-y-4"
              >
                {salesVisited && (
                  <>
                    {contextType && (
                      <div hidden={Boolean(selectedUnit)}>
                        <NativeMidtermPreparation
                          readEnabled={section === 'sales' && !selectedUnit}
                          key={`${String(session.generation)}-${contextType.id}-${cursor ?? 'first'}`}
                          workspaceRoomSelection={{
                            id: selectedRoom?.id ?? '',
                            onChange: (id) => {
                              selectRoom(id || null);
                            },
                          }}
                          runtime={runtime}
                          generation={session.generation}
                          type={contextType}
                          rooms={[
                            ...visibleRooms.filter((room) => room.room_type_id === contextType.id),
                            ...(selectedRoom &&
                            !visibleRooms.some((room) => room.id === selectedRoom.id)
                              ? [selectedRoom]
                              : []),
                          ]}
                          catalogComplete={!cursor && !nextCatalogCursor && !roomFilterActive}
                          catalogFiltered={roomFilterActive}
                          onGoExternal={() => {
                            selectSection('external');
                          }}
                        />
                      </div>
                    )}
                    {(selectedUnit ?? selectedRoom ?? contextType) && (
                      <LongMonthPricingSection
                        readEnabled={section === 'sales'}
                        showCommonTiers={!selectedUnit && !selectedRoom}
                        key={`${String(session.generation)}:${selectedUnit?.id ?? selectedRoom?.id ?? contextType?.id ?? ''}`}
                        runtime={runtime}
                        generation={session.generation}
                        kind={selectedUnit ? 'units' : selectedRoom ? 'rooms' : 'room-types'}
                        id={selectedUnit?.id ?? selectedRoom?.id ?? contextType?.id ?? ''}
                      />
                    )}
                    {!selectedTypeId &&
                      !selectedRoomId &&
                      !selectedUnitId &&
                      !contextType &&
                      !selectedRoom &&
                      !selectedUnit && (
                        <section className="rounded-md border p-4">
                          <h2 className="font-semibold">판매 준비</h2>
                          <p className="body-sm text-muted-foreground mt-2">
                            객실을 선택해 중기 일가격과 첫 입주일을 설정하고 판매 전환 결과를
                            확인하세요.
                          </p>
                          <NativeRoomSalesSection
                            readEnabled={section === 'sales'}
                            runtime={runtime}
                            propertyId={propertyId}
                            generation={session.generation}
                            onSelectRoom={selectRoom}
                            isCurrent={() =>
                              runtime.session.getSnapshot().generation === session.generation
                            }
                          />
                          <NativeUnitSalesSection
                            readEnabled={section === 'sales'}
                            runtime={runtime}
                            propertyId={propertyId}
                            generation={session.generation}
                            onSelectRoom={selectRoom}
                            onOpenSlot={(roomId, unitId) => {
                              // 기존 주소 규칙(section·room·slot)으로 그 슬롯의 외부 연동 패널을 연다.
                              const next = new URLSearchParams(params);
                              next.set('section', 'external');
                              next.set('room', roomId);
                              next.set('slot', unitId);
                              next.delete('type');
                              next.delete('cursor');
                              setParams(next);
                            }}
                            isCurrent={() =>
                              runtime.session.getSnapshot().generation === session.generation
                            }
                          />
                        </section>
                      )}
                    {contextType && runtime.typeSalesTransitionEnabled === true && propertyId && (
                      <div className="mt-2">
                        <TypeSalesTransitionSection
                          key={JSON.stringify([
                            runtime.apiNamespace,
                            session.generation,
                            propertyId,
                            contextType.id,
                          ])}
                          runtime={runtime}
                          propertyId={propertyId}
                          typeId={contextType.id}
                          typeName={contextType.name}
                          roomNames={Object.fromEntries(
                            current.rooms.map((room) => [room.id, room.name]),
                          )}
                          unitNames={Object.fromEntries(
                            current.rooms.flatMap((room) =>
                              room.units.map((unit) => [unit.id, unit.name]),
                            ),
                          )}
                        />
                      </div>
                    )}
                    <NativePublicSalesSection
                      readEnabled={section === 'sales'}
                      typeId={contextType?.id}
                      roomId={selectedRoom?.id}
                      unitId={selectedUnit?.id}
                      runtime={runtime}
                      generation={session.generation}
                      propertyId={propertyId}
                    />
                  </>
                )}
              </div>
              <div
                role="tabpanel"
                id="workspace-panel-spaces"
                aria-labelledby="workspace-tab-spaces"
                hidden={section !== 'spaces'}
                className="space-y-4"
              >
                <section className="rounded-md border p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2
                      ref={roomsHeadingRef}
                      tabIndex={-1}
                      className="scroll-mt-[calc(var(--workspace-sticky-height,0px)+1rem)] font-semibold"
                    >
                      {selectedType ? `${selectedType.name} 객실과 슬롯` : '객실과 슬롯'}
                    </h2>
                  </div>
                  {(selectedType ?? selectedRoom) && (
                    <p className="body-sm text-muted-foreground mt-2">
                      현재 페이지 객실 {visibleRooms.length}개 / 슬롯 {unitCount}개
                    </p>
                  )}
                  <div className="mt-3">
                    <SalesOrderControls
                      subject="객실"
                      nameLabel="호실 순"
                      searchPlaceholder="객실·슬롯명 검색"
                      floors={{
                        data: floorFacet.data,
                        isError: floorFacet.isError,
                        isFetching: floorFacet.isFetching,
                        retry: () => void floorFacet.refetch(),
                      }}
                      order={roomOrder}
                      onChange={changeRoomOrder}
                    />
                  </div>
                  <p className="body-sm text-muted-foreground mt-2">
                    객실 유형을 선택하면 해당 유형의 객실을 페이지별로 조회합니다.
                  </p>
                  <p className="body-sm text-muted-foreground mt-2">
                    객실이나 슬롯을 열고 홈페이지 콘텐츠에서 사진·문구의 직접 값을, 가격·판매에서
                    가격을 확인합니다.
                  </p>
                  {visibleRooms.length ? (
                    /* eslint-disable @mgrvtechgroup/admin-ds/no-hand-rolled-screen -- Bound long control labels only; DetailPage retains the screen width budget. */
                    <ul className="mt-2 flex flex-col gap-3">
                      {visibleRooms.map((room) => (
                        <li className="rounded-md border p-3" key={room.id}>
                          <h3
                            ref={selectedRoom?.id === room.id ? selectedRoomHeadingRef : undefined}
                            tabIndex={-1}
                            className="scroll-mt-[calc(var(--workspace-sticky-height,0px)+1rem)] font-medium"
                          >
                            {room.name} · {types.get(room.room_type_id) ?? '유형 확인 불가'}
                          </h3>
                          <p className="caption text-muted-foreground">
                            층 {room.floor ?? '—'} ·{' '}
                            {room.selected === null
                              ? '선정 정보 없음'
                              : room.selected
                                ? '관리 대상 선정'
                                : '관리 대상 미선정'}
                          </p>
                          <Button
                            className="mt-3 h-auto max-w-full [overflow-wrap:anywhere] whitespace-normal"
                            variant="outline"
                            aria-expanded={selectedRoom?.id === room.id}
                            onClick={() => {
                              selectRoom(selectedRoom?.id === room.id ? null : room.id, {
                                keepPage: true,
                              });
                            }}
                          >
                            {room.name} {selectedRoom?.id === room.id ? '상세 닫기' : '상세 보기'}
                          </Button>
                          {selectedRoom?.id === room.id && (
                            <div
                              role="group"
                              aria-label={`${room.name} 다음 작업`}
                              className="mt-3 flex flex-wrap gap-2"
                            >
                              {canConnectRooms && (
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    selectSection('external');
                                  }}
                                >
                                  {room.name} 이지렘스 연결
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                onClick={() => {
                                  // 객실 단위 버튼이므로 선택된 슬롯은 놓는다 — 슬롯을 남기면 슬롯 가격이 열린다.
                                  const next = new URLSearchParams(params);
                                  next.set('section', 'sales');
                                  next.delete('slot');
                                  setParams(next);
                                }}
                              >
                                {room.name} 가격·판매
                              </Button>
                            </div>
                          )}
                          <ul hidden={selectedRoom?.id !== room.id} className="mt-2 list-disc pl-5">
                            {room.units.length ? (
                              room.units.map((unit) => (
                                <li className="space-y-3 py-2" key={unit.id}>
                                  <h4
                                    ref={
                                      selectedUnit?.id === unit.id
                                        ? selectedUnitHeadingRef
                                        : undefined
                                    }
                                    tabIndex={-1}
                                    className="scroll-mt-[calc(var(--workspace-sticky-height,0px)+1rem)] font-medium"
                                  >
                                    {unit.name}
                                  </h4>
                                  {selectedRoom?.id === room.id && (
                                    <>
                                      <Button
                                        className="ml-3 h-auto max-w-full [overflow-wrap:anywhere] whitespace-normal"
                                        variant="outline"
                                        aria-expanded={selectedUnit?.id === unit.id}
                                        onClick={() => {
                                          selectUnit(selectedUnit?.id === unit.id ? null : unit.id);
                                        }}
                                      >
                                        {unit.name}{' '}
                                        {selectedUnit?.id === unit.id ? '상세 닫기' : '상세 보기'}
                                      </Button>
                                      {selectedUnit?.id === unit.id && (
                                        <div className="mt-2 ml-3 space-y-3">
                                          <span className="flex flex-wrap gap-2">
                                            <Button
                                              variant="outline"
                                              onClick={() => {
                                                selectSection('content');
                                              }}
                                            >
                                              {unit.name} 사진·문구 보기
                                            </Button>
                                            <Button
                                              variant="outline"
                                              onClick={() => {
                                                selectSection('sales');
                                              }}
                                            >
                                              {unit.name} 가격 확인
                                            </Button>
                                          </span>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </li>
                              ))
                            ) : (
                              <li>슬롯 없음</li>
                            )}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    /* eslint-enable @mgrvtechgroup/admin-ds/no-hand-rolled-screen */
                    <p className="mt-2">
                      {emptyFilteredTitle(
                        roomOrder,
                        '객실',
                        selectedType
                          ? '이 타입의 현재 페이지 객실이 없습니다.'
                          : '현재 페이지 객실이 없습니다.',
                      )}
                    </p>
                  )}
                </section>
                {nextCatalogCursor && (
                  <Button
                    disabled={catalog.isFetching}
                    onClick={() => {
                      const next = new URLSearchParams(params);
                      if (roomTypeId && propertyId) {
                        setRoomCursorScope({
                          propertyId,
                          typeId: roomTypeId,
                          cursor: nextCatalogCursor,
                          filterKey: roomFilterKey,
                        });
                      }
                      next.delete('room');
                      next.delete('slot');
                      next.set('cursor', nextCatalogCursor);
                      setParams(next);
                    }}
                  >
                    다음 객실
                  </Button>
                )}
                {catalogCursor && (
                  <Button
                    variant="outline"
                    disabled={catalog.isFetching}
                    onClick={() => {
                      const first = new URLSearchParams(params);
                      first.delete('cursor');
                      first.delete('room');
                      first.delete('slot');
                      setParams(first);
                    }}
                  >
                    첫 객실 페이지
                  </Button>
                )}
              </div>
              <div
                role="tabpanel"
                id="workspace-panel-access"
                aria-labelledby="workspace-tab-access"
                hidden={section !== 'access'}
                className="space-y-4"
              >
                <AccessCoverage />
                {section === 'access' && (
                  <PropertyAccessOverview
                    key={propertyId}
                    runtime={runtime}
                    propertyId={propertyId}
                    generation={session.generation}
                    onRoom={selectRoom}
                  />
                )}
                {selectedRoomId ? (
                  section === 'access' && (
                    <RoomOnlineAccessSection
                      key={selectedRoomId}
                      runtime={runtime}
                      generation={session.generation}
                      propertyId={propertyId}
                      roomId={selectedRoomId}
                      isCurrent={() => unitSelection.current.roomId === selectedRoomId}
                    />
                  )
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => {
                      selectSection('spaces');
                    }}
                  >
                    객실 선택
                  </Button>
                )}
              </div>
            </>
          ) : null}
        </div>
      </DetailPage>
    );
  }
  return <NativePropertyListPage runtime={runtime} />;
}
